/**
 * Financing Leads Manager - Admin Interface
 * Manage financing leads, assign to banks, track commissions
 * For Wellington Woods & Associates and Sebastian Alliance Group administrators
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../ui/alert-dialog';
import {
  Building2,
  Users,
  DollarSign,
  FileText,
  Download,
  Eye,
  CheckCircle,
  Clock,
  XCircle,
  Search,
  RefreshCw,
  PlusCircle,
  Landmark,
  Trash2,
  Edit,
  Send,
  TrendingUp,
  Filter
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const FinancingLeadsManager = () => {
  const [leads, setLeads] = useState([]);
  const [bankingPartners, setBankingPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedLead, setSelectedLead] = useState(null);
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);
  const [showAddBankModal, setShowAddBankModal] = useState(false);
  const [stats, setStats] = useState({});

  // New lead form
  const [newLead, setNewLead] = useState({
    full_name: '',
    email: '',
    phone: '',
    company_name: '',
    financing_type: 'renovation',
    property_address: '',
    estimated_property_value: '',
    loan_amount_requested: '',
    business_summary: '',
    assigned_bank_id: ''
  });

  // New bank form
  const [newBank, setNewBank] = useState({
    bank_name: '',
    contact_name: '',
    email: '',
    password: '',
    phone: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [leadsRes, banksRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/admin/financing/leads`, { headers }),
        axios.get(`${API}/api/admin/financing/banking-partners`, { headers }),
        axios.get(`${API}/api/admin/financing/stats`, { headers })
      ]);

      setLeads(leadsRes.data.leads || []);
      setBankingPartners(banksRes.data.partners || []);
      setStats(statsRes.data || {});
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load financing data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddLead = async () => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${API}/api/admin/financing/leads`, newLead, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Lead added successfully');
      setShowAddLeadModal(false);
      setNewLead({
        full_name: '',
        email: '',
        phone: '',
        company_name: '',
        financing_type: 'renovation',
        property_address: '',
        estimated_property_value: '',
        loan_amount_requested: '',
        business_summary: '',
        assigned_bank_id: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add lead');
    }
  };

  const handleAddBank = async () => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${API}/api/admin/financing/banking-partners`, newBank, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Banking partner added successfully');
      setShowAddBankModal(false);
      setNewBank({
        bank_name: '',
        contact_name: '',
        email: '',
        password: '',
        phone: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add banking partner');
    }
  };

  const handleAssignBank = async (leadId, bankId) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.patch(
        `${API}/api/admin/financing/leads/${leadId}/assign`,
        { bank_id: bankId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Lead assigned to bank');
      fetchData();
    } catch (error) {
      toast.error('Failed to assign lead');
    }
  };

  const handleDeleteLead = async (leadId) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`${API}/api/admin/financing/leads/${leadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Lead deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete lead');
    }
  };

  const handleDeleteBank = async (bankId) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`${API}/api/admin/financing/banking-partners/${bankId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Banking partner deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete banking partner');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      new: { color: 'bg-blue-100 text-blue-800', icon: Clock },
      in_review: { color: 'bg-yellow-100 text-yellow-800', icon: Eye },
      approved: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      funded: { color: 'bg-emerald-100 text-emerald-800', icon: DollarSign },
      rejected: { color: 'bg-red-100 text-red-800', icon: XCircle }
    };
    const config = statusConfig[status] || statusConfig.new;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {status?.replace('_', ' ').toUpperCase() || 'NEW'}
      </Badge>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const filteredLeads = leads.filter(lead => {
    const matchesSearch =
      lead.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.property_address?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || lead.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-sm text-gray-500">Total Leads</p>
              <p className="text-2xl font-bold">{stats.total_leads || 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-sm text-gray-500">Funded</p>
              <p className="text-2xl font-bold text-emerald-600">{stats.funded || 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-sm text-gray-500">Total Loan Value</p>
              <p className="text-xl font-bold text-blue-600">{formatCurrency(stats.total_loan_value)}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-emerald-50">
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-sm text-emerald-600">Commission (1%)</p>
              <p className="text-xl font-bold text-emerald-700">{formatCurrency(stats.total_commission)}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-blue-50">
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-sm text-blue-600">Wellington Woods</p>
              <p className="text-xl font-bold text-blue-700">{formatCurrency((stats.total_commission || 0) * 0.5)}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-purple-50">
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-sm text-purple-600">Sebastian Alliance</p>
              <p className="text-xl font-bold text-purple-700">{formatCurrency((stats.total_commission || 0) * 0.5)}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Banking Partners Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Landmark className="h-5 w-5 text-blue-600" />
                Banking Partners
              </CardTitle>
              <CardDescription>Manage partner banks for financing</CardDescription>
            </div>
            <Button onClick={() => setShowAddBankModal(true)} className="bg-blue-600">
              <PlusCircle className="h-4 w-4 mr-2" />
              Add Bank Partner
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {bankingPartners.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No banking partners added yet</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {bankingPartners.map((bank) => (
                <Card key={bank.id} className="border-2">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                          {bank.logo ? (
                            <img src={bank.logo} alt={bank.bank_name} className="w-8 h-8" />
                          ) : (
                            <Landmark className="h-6 w-6 text-blue-600" />
                          )}
                        </div>
                        <div>
                          <h4 className="font-semibold">{bank.bank_name}</h4>
                          <p className="text-sm text-gray-500">{bank.contact_name}</p>
                        </div>
                      </div>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="sm" className="text-red-600">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Banking Partner?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will remove {bank.bank_name} from the system. Existing leads will remain but won't be assigned to this bank.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDeleteBank(bank.id)}
                              className="bg-red-600"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                    <div className="text-sm text-gray-600 space-y-1">
                      <p>📧 {bank.email}</p>
                      <p>📱 {bank.phone || 'N/A'}</p>
                      <p className="text-emerald-600 font-medium">
                        {bank.lead_count || 0} leads assigned
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Financing Leads Section */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-emerald-600" />
                Financing Leads ({filteredLeads.length})
              </CardTitle>
              <CardDescription>Manage and assign financing leads to banking partners</CardDescription>
            </div>
            <div className="flex gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search leads..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 w-64"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border rounded-md text-sm"
              >
                <option value="all">All Status</option>
                <option value="new">New</option>
                <option value="in_review">In Review</option>
                <option value="approved">Approved</option>
                <option value="funded">Funded</option>
                <option value="rejected">Rejected</option>
              </select>
              <Button onClick={() => setShowAddLeadModal(true)} className="bg-emerald-600">
                <PlusCircle className="h-4 w-4 mr-2" />
                Add Lead
              </Button>
              <Button variant="outline" onClick={fetchData}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredLeads.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No financing leads found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Client</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Property</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Loan Amount</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Commission (1%)</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Assigned Bank</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Status</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLeads.map((lead) => (
                    <tr key={lead.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-medium text-gray-900">{lead.full_name}</p>
                          <p className="text-sm text-gray-500">{lead.email}</p>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <p className="text-sm text-gray-700 max-w-xs truncate">{lead.property_address}</p>
                        <p className="text-xs text-gray-500 capitalize">{lead.financing_type?.replace('_', ' ')}</p>
                      </td>
                      <td className="py-3 px-4">
                        <p className="font-semibold text-gray-900">{formatCurrency(lead.loan_amount_requested)}</p>
                        <p className="text-xs text-gray-500">Value: {formatCurrency(lead.estimated_property_value)}</p>
                      </td>
                      <td className="py-3 px-4">
                        <p className="font-semibold text-emerald-600">{formatCurrency(lead.loan_amount_requested * 0.01)}</p>
                        <p className="text-xs text-gray-500">Split: 50/50</p>
                      </td>
                      <td className="py-3 px-4">
                        <select
                          value={lead.assigned_bank_id || ''}
                          onChange={(e) => handleAssignBank(lead.id, e.target.value)}
                          className="text-sm border rounded px-2 py-1"
                        >
                          <option value="">Unassigned</option>
                          {bankingPartners.map((bank) => (
                            <option key={bank.id} value={bank.id}>{bank.bank_name}</option>
                          ))}
                        </select>
                      </td>
                      <td className="py-3 px-4">
                        {getStatusBadge(lead.status)}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setSelectedLead(lead)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="sm" variant="outline" className="text-red-600">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Lead?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will permanently delete this financing lead.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteLead(lead.id)}
                                  className="bg-red-600"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Lead Modal */}
      <AlertDialog open={showAddLeadModal} onOpenChange={setShowAddLeadModal}>
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Add New Financing Lead</AlertDialogTitle>
            <AlertDialogDescription>
              Manually add a financing lead to the system
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div>
              <label className="text-sm font-medium">Full Name *</label>
              <Input
                value={newLead.full_name}
                onChange={(e) => setNewLead({ ...newLead, full_name: e.target.value })}
                placeholder="John Smith"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Email *</label>
              <Input
                type="email"
                value={newLead.email}
                onChange={(e) => setNewLead({ ...newLead, email: e.target.value })}
                placeholder="john@example.com"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Phone *</label>
              <Input
                value={newLead.phone}
                onChange={(e) => setNewLead({ ...newLead, phone: e.target.value })}
                placeholder="+1 (242) 555-0123"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Company</label>
              <Input
                value={newLead.company_name}
                onChange={(e) => setNewLead({ ...newLead, company_name: e.target.value })}
                placeholder="Company Name"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Financing Type</label>
              <select
                value={newLead.financing_type}
                onChange={(e) => setNewLead({ ...newLead, financing_type: e.target.value })}
                className="w-full border rounded px-3 py-2"
              >
                <option value="renovation">Renovation</option>
                <option value="new_construction">New Construction</option>
                <option value="refinance">Refinance</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">Assign to Bank</label>
              <select
                value={newLead.assigned_bank_id}
                onChange={(e) => setNewLead({ ...newLead, assigned_bank_id: e.target.value })}
                className="w-full border rounded px-3 py-2"
              >
                <option value="">Select Bank</option>
                {bankingPartners.map((bank) => (
                  <option key={bank.id} value={bank.id}>{bank.bank_name}</option>
                ))}
              </select>
            </div>
            <div className="col-span-2">
              <label className="text-sm font-medium">Property Address *</label>
              <Input
                value={newLead.property_address}
                onChange={(e) => setNewLead({ ...newLead, property_address: e.target.value })}
                placeholder="123 Main Street, Nassau, Bahamas"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Property Value *</label>
              <Input
                type="number"
                value={newLead.estimated_property_value}
                onChange={(e) => setNewLead({ ...newLead, estimated_property_value: e.target.value })}
                placeholder="5000000"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Loan Amount *</label>
              <Input
                type="number"
                value={newLead.loan_amount_requested}
                onChange={(e) => setNewLead({ ...newLead, loan_amount_requested: e.target.value })}
                placeholder="2000000"
              />
            </div>
            <div className="col-span-2">
              <label className="text-sm font-medium">Business Summary</label>
              <textarea
                value={newLead.business_summary}
                onChange={(e) => setNewLead({ ...newLead, business_summary: e.target.value })}
                placeholder="Describe the financing purpose..."
                className="w-full border rounded px-3 py-2 h-24"
              />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button onClick={handleAddLead} className="bg-emerald-600">
              Add Lead
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add Bank Modal */}
      <AlertDialog open={showAddBankModal} onOpenChange={setShowAddBankModal}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Add Banking Partner</AlertDialogTitle>
            <AlertDialogDescription>
              Add a new banking partner to receive financing leads
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Bank Name *</label>
              <Input
                value={newBank.bank_name}
                onChange={(e) => setNewBank({ ...newBank, bank_name: e.target.value })}
                placeholder="Sterling Bank"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Contact Name *</label>
              <Input
                value={newBank.contact_name}
                onChange={(e) => setNewBank({ ...newBank, contact_name: e.target.value })}
                placeholder="John Banker"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Email * (Login credentials)</label>
              <Input
                type="email"
                value={newBank.email}
                onChange={(e) => setNewBank({ ...newBank, email: e.target.value })}
                placeholder="partner@bank.com"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Password *</label>
              <Input
                type="password"
                value={newBank.password}
                onChange={(e) => setNewBank({ ...newBank, password: e.target.value })}
                placeholder="••••••••"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Phone</label>
              <Input
                value={newBank.phone}
                onChange={(e) => setNewBank({ ...newBank, phone: e.target.value })}
                placeholder="+1 (242) 555-0100"
              />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button onClick={handleAddBank} className="bg-blue-600">
              Add Banking Partner
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Lead Detail Modal */}
      {selectedLead && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Lead Details</CardTitle>
                <Button variant="ghost" onClick={() => setSelectedLead(null)}>✕</Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Full Name</p>
                  <p className="font-medium">{selectedLead.full_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="font-medium">{selectedLead.email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Phone</p>
                  <p className="font-medium">{selectedLead.phone}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Company</p>
                  <p className="font-medium">{selectedLead.company_name || 'N/A'}</p>
                </div>
              </div>

              <hr />

              <div>
                <p className="text-sm text-gray-500">Property Address</p>
                <p className="font-medium">{selectedLead.property_address}</p>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Property Value</p>
                  <p className="font-medium text-blue-600">{formatCurrency(selectedLead.estimated_property_value)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Loan Requested</p>
                  <p className="font-medium text-emerald-600">{formatCurrency(selectedLead.loan_amount_requested)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">LTV Ratio</p>
                  <p className="font-medium">{((selectedLead.loan_amount_requested / selectedLead.estimated_property_value) * 100).toFixed(1)}%</p>
                </div>
              </div>

              <div className="bg-emerald-50 p-4 rounded-lg">
                <p className="text-sm text-emerald-600 font-medium">Commission Breakdown (1%)</p>
                <div className="grid grid-cols-3 gap-4 mt-2">
                  <div>
                    <p className="text-xs text-gray-500">Total Commission</p>
                    <p className="font-bold text-emerald-700">{formatCurrency(selectedLead.loan_amount_requested * 0.01)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Wellington Woods (50%)</p>
                    <p className="font-bold text-blue-700">{formatCurrency(selectedLead.loan_amount_requested * 0.005)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Sebastian Alliance (50%)</p>
                    <p className="font-bold text-purple-700">{formatCurrency(selectedLead.loan_amount_requested * 0.005)}</p>
                  </div>
                </div>
              </div>

              <div>
                <p className="text-sm text-gray-500">Business Summary</p>
                <p className="text-sm bg-gray-50 p-3 rounded-lg">{selectedLead.business_summary || 'No summary provided'}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default FinancingLeadsManager;
